import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Crosshair, RotateCcw } from "lucide-react";
import clsx from "clsx";
import { PanelSelect } from "../../../PanelSelect";
import { ANALYTICS_EVENTS, trackEvent } from "utils/analytics";
import type { RemovedDeviceRowProps } from "./types";

export const RemovedDeviceRow = ({
  device,
  name,
  iconDataUrl,
  isPinned,
  circuitOptions,
  onPin,
  onRestore,
  onAssign,
  onHoverIn,
  onHoverOut,
}: RemovedDeviceRowProps) => {
  const { t } = useTranslation();

  const options = useMemo(
    () =>
      circuitOptions.map((option) => ({
        value: option.circuitId,
        label: option.label,
        color: option.color,
      })),
    [circuitOptions],
  );

  const canMove = Boolean(onAssign) && options.length > 0;

  return (
    <li
      data-testid={`removed-device-${device.destinationId}`}
      onMouseEnter={onHoverIn}
      onMouseLeave={onHoverOut}
      className={clsx(
        "flex items-center gap-1 rounded border-t border-slate-100 px-1 py-1 first:border-t-0",
        isPinned ? "bg-rose-50" : "hover:bg-slate-50",
      )}
    >
      <button
        type="button"
        onClick={onPin}
        title={t("smartSurveyor.cablesBeta.stepShowOnPlan")}
        className="flex min-w-0 flex-1 items-center gap-2 text-left"
      >
        {iconDataUrl ? (
          // Titled rather than described: the name is already the next thing on
          // the row, so an alt would read it twice.
          <img
            src={iconDataUrl}
            alt=""
            title={name}
            draggable={false}
            className="h-8 w-8 flex-none rounded border border-slate-200 object-cover"
          />
        ) : (
          <span className="grid h-8 w-8 flex-none place-items-center rounded border border-slate-200 bg-slate-50">
            <span className="h-1.5 w-1.5 rounded-full bg-slate-500" />
          </span>
        )}
        <span className="min-w-0 flex-1 truncate text-[14px] font-medium text-slate-800">
          {name}
        </span>
        <span className="flex-none rounded bg-slate-100 px-1.5 py-0.5 text-[13px] font-medium tabular-nums text-slate-700">
          {device.circuitId}
        </span>
        {isPinned && (
          <Crosshair size={15} className="flex-none text-rose-600" />
        )}
      </button>

      {/* Both glossed icons, on the name's own line. Thirteen struck devices
          was thirteen full-width "Put back" buttons and thirteen extra rows —
          "lot of buttons and lot of divs" in its purest form. The worded-control
          rule this reverses was about UNGLOSSED glyphs; ↺ carries its title and
          ⇄ carries the heading of the list it opens, which is the gloss the
          rule actually asks for. */}
      <button
        type="button"
        onClick={() => {
          trackEvent(
            ANALYTICS_EVENTS.SMART_SURVEYOR_CABLE_RUN_DEVICES_CHANGED,
            {
              action: "restored",
              deviceCount: 1,
            },
          );
          onRestore();
        }}
        title={t("smartSurveyor.cablesBeta.removedGroupRestore")}
        className="grid h-9 w-9 flex-none place-items-center rounded-md border border-transparent text-blue-700 hover:border-blue-300 hover:bg-blue-50"
      >
        <RotateCcw size={16} />
      </button>

      {canMove && (
        <PanelSelect
          value=""
          options={options}
          trigger="icon"
          placeholder={t("smartSurveyor.cablesBeta.removedDeviceMoveOnto")}
          ariaLabel={t("smartSurveyor.cablesBeta.removedDeviceMoveOnto")}
          onPick={(circuitId) => {
            trackEvent(
              ANALYTICS_EVENTS.SMART_SURVEYOR_CABLE_RUN_DEVICES_CHANGED,
              { action: "moved", deviceCount: 1 },
            );
            onAssign?.(circuitId);
          }}
        />
      )}
    </li>
  );
};
